import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../../../../../../api/hooks';
import Modal from '../../../controls/Modal';
import { PersonFillIcon } from '../../../../model/icons';
import { participantName, formatDt, CODE_TYPE_LABEL, CODE_TYPES } from './participantsUtils';
import { CodeEventRow, TaskOperationRow, CoinTransferRow, ExternalAccrualRow } from './ParticipantDetailRows';
import { AnsweredQuestionView } from '../CodesPanel';
import BalanceEditModal from './BalanceEditModal';

const TABS = [
    { key: 'codes', label: 'Коды' },
    { key: 'tasks', label: 'Задания' },
    { key: 'transfers', label: 'Переводы' },
    { key: 'external', label: 'Внешние начисления' }
];

// Модалка участника «Насоли соседу»: сводка + табы (коды / задания / переводы).
export default function ParticipantDetailModal({ participant, smenaId, qrBaseUrl, onClose }) {
    const { userId, stateId, teamName, teamColor, balance, totalBalance, coinBalance, achieveDailyPresent, confirmedAt, telegramId } = participant;
    const queryClient = useQueryClient();
    const [tab, setTab] = useState('codes');
    const [codeType, setCodeType] = useState(null);
    const [answered, setAnswered] = useState(null); // id фактически выпавшего вопроса
    const [editBal, setEditBal] = useState(false);
    const [bal, setBal] = useState({ balance, totalBalance, coinBalance });

    const onBalSaved = (next) => {
        setBal(next);
        queryClient.invalidateQueries({ queryKey: ['neighbor', 'participant-details', smenaId, userId] });
        queryClient.invalidateQueries({ queryKey: ['neighbor', 'participants', smenaId] });
    };

    const { data: details, isLoading } = useApiQuery(
        ['neighbor', 'participant-details', smenaId, userId],
        `/api/neighbor/admin/participants/find-details?smenaId=${smenaId}&userId=${userId}`,
        { refetchOnMount: 'always', enabled: !!smenaId && !!userId }
    );

    const summary = details?.summary;
    // Новые сверху — бэкенд уже так отдаёт, сортируем на клиенте защитно (как и в других играх).
    const codeEvents = [...(details?.codeEvents || [])].sort((a, b) => new Date(b.activatedAt) - new Date(a.activatedAt));
    const taskOperations = [...(details?.taskOperations || [])].sort((a, b) => new Date(b.buyTime) - new Date(a.buyTime));
    const coinTransfers = [...(details?.coinTransfers || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const externalAccruals = [...(details?.externalAccruals || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const presentCodeTypes = CODE_TYPES.filter(t => codeEvents.some(ev => ev.codeType === t));
    const activeCodeType = codeType && presentCodeTypes.includes(codeType) ? codeType : presentCodeTypes[0];
    const shownCodeEvents = activeCodeType ? codeEvents.filter(ev => ev.codeType === activeCodeType) : [];

    const tabCount = (key) => {
        if (key === 'codes') return codeEvents.length;
        if (key === 'tasks') return taskOperations.length;
        if (key === 'transfers') return coinTransfers.length;
        return externalAccruals.length;
    };

    return (
        <>
        <Modal
            open
            kicker="Насоли соседу — участник"
            title={participantName(participant)}
            size="xl"
            onClose={onClose}
            footer={<button type="button" className="btn btn-ghost" onClick={onClose}>Закрыть</button>}
        >
            <div className="sp-part-head2">
                <span className="sp-part-avatar lg" style={{ background: teamColor || 'var(--navy)' }}><PersonFillIcon /></span>
                <div className="sp-part-info">
                    <div className="sp-part-meta">
                        {teamName && (
                            <span className="sp-part-meta-item"><span className="bdot" style={{ background: teamColor || 'var(--line-2)' }} />{teamName}</span>
                        )}
                        <span className="sp-part-meta-item">Подтвердил {formatDt(confirmedAt)}</span>
                        {telegramId && <span className="sp-part-meta-item">Telegram ID: {telegramId}</span>}
                        {achieveDailyPresent && <span className="sp-part-daily">бонус дня получен</span>}
                    </div>
                </div>
                <div className="sp-bal-row">
                    <div className="sp-bal now"><span className="l">Баланс</span><span className="v">{bal.balance ?? 0}</span></div>
                    <div className="sp-bal total"><span className="l">Всего заработано</span><span className="v">{bal.totalBalance ?? 0}</span></div>
                    <div className="sp-bal coin"><span className="l">Монеты</span><span className="v">{Number(bal.coinBalance ?? 0).toFixed(1)}</span></div>
                    {stateId != null && (
                        <button type="button" className="btn btn-ghost btn-sm sp-bal-edit" onClick={() => setEditBal(true)}>Редактировать балансы</button>
                    )}
                </div>
            </div>

            {summary && (
                <div className="sp-stats-table">
                    <div className="row"><span className="k">Кодов введено</span><span className="v">{summary.codesActivated}</span></div>
                    <div className="row"><span className="k">Баллов с кодов</span><span className="v">{summary.pointsFromCodes}</span></div>
                    <div className="row"><span className="k">Заданий куплено</span><span className="v">{summary.tasksBought}</span></div>
                    <div className="row"><span className="k">Потрачено на задания</span><span className="v">{summary.pointsSpentOnTasks}</span></div>
                    <div className="row"><span className="k">Переведено другим</span><span className="v">{summary.pointsTransferredOut}</span></div>
                    <div className="row"><span className="k">Получено переводом</span><span className="v">{summary.pointsTransferredIn}</span></div>
                    <div className="row full"><span className="k">Начислено извне</span><span className="v">{summary.pointsFromExternal ?? 0}</span></div>
                </div>
            )}

            <div className="bt-tabs sp-part-tabs">
                {TABS.map(t => (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>
                        {t.label} <span className="fr-cnt">({tabCount(t.key)})</span>
                    </a>
                ))}
            </div>

            {isLoading ? (
                <div className="bt-empty">Загрузка…</div>
            ) : (
                <>
                    {tab === 'codes' && (
                        codeEvents.length > 0 ? (
                            <>
                                <div className="sp-chips sp-part-subchips">
                                    {presentCodeTypes.map(t => {
                                        const count = codeEvents.filter(ev => ev.codeType === t).length;
                                        return (
                                            // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                                            <span
                                                key={t}
                                                className={`sp-chip${activeCodeType === t ? ' on' : ''}`}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setCodeType(t)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') setCodeType(t); }}
                                            >
                                                {CODE_TYPE_LABEL[t] || t} <span className="fr-cnt">({count})</span>
                                            </span>
                                        );
                                    })}
                                </div>
                                <div className="sp-codegrid">{shownCodeEvents.map(ev => <CodeEventRow key={ev.id} event={ev} onOpenAnswered={setAnswered} qrBaseUrl={qrBaseUrl} />)}</div>
                            </>
                        ) : <div className="bt-empty">Кодов не вводил</div>
                    )}
                    {tab === 'tasks' && (
                        taskOperations.length > 0
                            ? <div className="sp-part-list">{taskOperations.map(op => <TaskOperationRow key={op.id} op={op} />)}</div>
                            : <div className="bt-empty">Операций с заданиями нет</div>
                    )}
                    {tab === 'transfers' && (
                        coinTransfers.length > 0
                            ? <div className="sp-part-list">{coinTransfers.map(t => <CoinTransferRow key={t.id} transfer={t} />)}</div>
                            : <div className="bt-empty">Переводов нет</div>
                    )}
                    {tab === 'external' && (
                        externalAccruals.length > 0
                            ? <div className="sp-part-list">{externalAccruals.map(a => <ExternalAccrualRow key={a.id} accrual={a} />)}</div>
                            : <div className="bt-empty">Начислений извне нет — учёт ведётся с 08.07.2026, более ранние начисления (например, со Ставок) сюда не попадают</div>
                    )}
                </>
            )}
        </Modal>
        {answered != null && <AnsweredQuestionView id={answered} qrBaseUrl={qrBaseUrl} onClose={() => setAnswered(null)} />}
        {editBal && stateId != null && (
            <BalanceEditModal stateId={stateId} values={bal} onClose={() => setEditBal(false)} onSaved={onBalSaved} />
        )}
        </>
    );
}

ParticipantDetailModal.propTypes = {
    participant: PropTypes.shape({
        userId: PropTypes.number.isRequired,
        stateId: PropTypes.number,
        username: PropTypes.string,
        name: PropTypes.string,
        surname: PropTypes.string,
        teamName: PropTypes.string,
        teamColor: PropTypes.string,
        telegramId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        balance: PropTypes.number,
        totalBalance: PropTypes.number,
        coinBalance: PropTypes.number,
        achieveDailyPresent: PropTypes.bool,
        confirmedAt: PropTypes.string
    }).isRequired,
    smenaId: PropTypes.number.isRequired,
    qrBaseUrl: PropTypes.string,
    onClose: PropTypes.func.isRequired
};

ParticipantDetailModal.defaultProps = { qrBaseUrl: null };
